// src/components/asistencia/faceModels.js
// Utilidades compartidas de @vladmandic/face-api para el módulo de Asistencia.
// Carga los modelos UNA sola vez (flag de módulo) y expone helpers comunes.
// Todo corre en el navegador (Q0 por uso).
import * as faceapi from '@vladmandic/face-api';
import { FilesetResolver, FaceLandmarker } from '@mediapipe/tasks-vision';

const MODEL_URL = '/models';
let _cargados = false;

export async function cargarModelos() {
  if (_cargados) return;
  await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
  await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
  await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
  _cargados = true;
  // Precarga del modelo de expresiones (MediaPipe) en segundo plano, sin bloquear:
  // así ya está caliente cuando llega la primera marca.
  cargarLandmarker().catch(() => { /* se reintenta al usarse */ });
}

// --- MediaPipe Face Landmarker (modelo de expresiones, para la prueba de vida) ---
// Servido desde public/models/mediapipe/ (modelo .task ~3.6MB + WASM), igual que
// los modelos de face-api: sin depender de CDNs externos. Licencia Apache 2.0, Q0.
let _landmarker = null;
let _cargandoLandmarker = null;
export function cargarLandmarker() {
  if (_landmarker) return Promise.resolve(_landmarker);
  if (!_cargandoLandmarker) {
    _cargandoLandmarker = (async () => {
      const fileset = await FilesetResolver.forVisionTasks('/models/mediapipe/wasm');
      _landmarker = await FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: '/models/mediapipe/face_landmarker.task' },
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: false,
        runningMode: 'VIDEO',
        numFaces: 1
      });
      return _landmarker;
    })().catch((e) => { _cargandoLandmarker = null; throw e; });
  }
  return _cargandoLandmarker;
}

export function opcionesDeteccion() {
  return new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });
}

// Detección de rostro con TOPE de tiempo. En teléfonos con el WebGL dañado la
// detección puede COLGARSE (la promesa nunca resuelve) y el flujo de marcar quedaba
// pegado en "Verificando tu rostro…" para siempre, dejando al colaborador SIN marca
// (caso real 25/08/2026). Si no responde en `ms`, devuelve null y el que llama sigue.
export function detectarRostroConTope(video, ms = 4000) {
  const tarea = faceapi.detectSingleFace(video, opcionesDeteccion()).withFaceLandmarks().withFaceDescriptor();
  return Promise.race([
    Promise.resolve(tarea).then((det) => det || null).catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(null), ms))
  ]);
}

// Dibuja una fuente (video o imagen) a un canvas ~480px de ancho → Blob WebP.
export function fotoWebpDesde(source, srcW, srcH) {
  return new Promise((resolve) => {
    const targetW = 480;
    const ratio = (srcW && srcH) ? srcH / srcW : 0.75;
    const w = targetW;
    const h = Math.round(targetW * ratio) || 360;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(source, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (blob) { resolve(blob); return; }
      canvas.toBlob((b2) => resolve(b2), 'image/jpeg', 0.85); // fallback
    }, 'image/webp', 0.8);
  });
}

// --- Prueba de vida (liveness) por SONRISA, con MediaPipe blendshapes ---
// HISTORIA: con face-api intentamos geometría artesanal (EAR de ojos, ancho de boca)
// y falló en ambas direcciones (no vio ojos cerrados; "vio" sonrisas que no existían).
// MediaPipe Face Landmarker entrega directamente PUNTAJES de expresión (blendshapes
// 0..1): mouthSmileLeft/Right y eyeBlinkLeft/Right, mucho más confiables.
// Pasa si: el puntaje de sonrisa CAMBIA lo suficiente (delta ≥0.35 entre su mínimo
// y su máximo, en cualquier dirección — una foto no cambia de expresión), o como
// respaldo silencioso, un parpadeo natural completo.
// 🔴 Por qué DELTA y no umbrales fijos (25/08/2026, medido con marcas reales): la
// regla vieja exigía ver cara seria (≤0.30) ANTES de la sonrisa (≥0.55). A quien su
// cara NEUTRAL le puntúa >0.30 (pasa: se midió una colaboradora atrapada los 10s
// completos con mínimo 0.45 y sonrisa hasta 0.85) el "reposo" jamás se registraba y
// quedaba en el vaivén "sonreí / dejá de sonreír" hasta el timeout. Con el delta, la
// vara es el cambio contra SU propia cara, no contra un número fijo.
const _score = (cats, nombre) => { const c = cats.find((x) => x.categoryName === nombre); return c ? c.score : 0; };

// Devuelve { ok, via, smileMin, smileMax, frames } — el diagnóstico se guarda en la
// marca. NUNCA lanza error (si algo falla, ok: false). El fallo es SILENCIOSO para
// el empleado (decisión de Toto): la marca entra normal y RRHH la ve en Revisión.
// FLUJO GUIADO (idea de Toto): onEstado(estado) avisa a la UI qué pedirle a la persona:
//   'SONRIA'         -> "¡Sonreíle a la cámara!"
//   'PRIMERO_SERIO'  -> ya venía sonriendo: "Dejá de sonreír un momento… y volvé a sonreír"
// Así se resuelve interactivamente el caso "ya estaba sonriendo desde el inicio"
// (sin la guía, sería indistinguible de una foto sonriente y caería a Revisión).
export async function detectarPruebaVida(video, { timeoutMs = 10000, onEstado } = {}) {
  const r4 = (v) => Number(v.toFixed(4));
  let lm = null;
  try { lm = await cargarLandmarker(); } catch (e) { console.error('No se pudo cargar el modelo de expresiones:', e); }
  if (!lm) return { ok: false, via: 'SIN_MODELO', smileMin: null, smileMax: null, frames: 0 };
  let ultimoEstado = null;
  const avisar = (e) => { if (e !== ultimoEstado) { ultimoEstado = e; if (onEstado) { try { onEstado(e); } catch (err) { /* noop */ } } } };
  const inicio = Date.now();
  let smileMin = Infinity; let smileMax = 0; let frames = 0;
  let cierreMin = Infinity; let cierreMax = 0; // rango de ojos (para detectar cámara congelada)
  let sonriendoSeguidos = 0;      // cuadros seguidos sonriendo (para la guía "primero serio")
  let ojosAbiertosVistos = false; // para el respaldo de parpadeo
  let vioOjosCerrados = false;
  avisar('SONRIA');
  while (Date.now() - inicio < timeoutMs) {
    try {
      const res = lm.detectForVideo(video, performance.now());
      const cats = res && res.faceBlendshapes && res.faceBlendshapes[0] && res.faceBlendshapes[0].categories;
      if (cats && cats.length) {
        frames++;
        const sonrisa = (_score(cats, 'mouthSmileLeft') + _score(cats, 'mouthSmileRight')) / 2;
        const cierreOjos = (_score(cats, 'eyeBlinkLeft') + _score(cats, 'eyeBlinkRight')) / 2;
        if (sonrisa < smileMin) smileMin = sonrisa;
        if (sonrisa > smileMax) smileMax = sonrisa;
        if (cierreOjos < cierreMin) cierreMin = cierreOjos;
        if (cierreOjos > cierreMax) cierreMax = cierreOjos;
        // Gesto principal: CAMBIO de expresión (delta contra su propia cara, en
        // cualquier dirección). Llegar sonriendo y ponerse serio también cuenta —
        // ya no hace falta el baile "serio → volver a sonreír". frames >= 3 evita
        // decidir con un solo cuadro borroso de entrada.
        if (frames >= 3 && smileMax - smileMin >= 0.35) {
          return { ok: true, via: 'SONRISA', smileMin: r4(smileMin), smileMax: r4(smileMax), frames };
        }
        // Guía en pantalla: si viene sonriendo, pedirle ponerse serio (con eso alcanza).
        if (sonrisa >= 0.45) {
          sonriendoSeguidos++;
          if (sonriendoSeguidos >= 2) avisar('PRIMERO_SERIO');
        } else if (sonrisa <= 0.3) {
          sonriendoSeguidos = 0;
          avisar('SONRIA');
        }
        // Respaldo silencioso: parpadeo natural completo (abierto -> cerrado -> abierto).
        // Umbral de cierre 0.45: a ~10 fps el cuadro muestreado puede agarrar el
        // párpado a medio cerrar; el puntaje de MediaPipe es gradual.
        if (cierreOjos >= 0.45) {
          if (ojosAbiertosVistos) vioOjosCerrados = true;
        } else if (cierreOjos <= 0.3) {
          if (vioOjosCerrados) return { ok: true, via: 'PARPADEO', smileMin: r4(smileMin), smileMax: r4(smileMax), frames };
          ojosAbiertosVistos = true;
        }
        // Cámara CONGELADA: la imagen quedó clavada en un cuadro fijo → los puntajes
        // no se mueven NADA (casos reales medidos el 25/08/2026: rango ≤0.0006 en
        // 130-280 cuadros; una cara viva jitterea muy por encima de 0.005 solo por
        // ruido del sensor). Se corta temprano para avisarle a la persona en vez de
        // dejarla sonriéndole 10 segundos a un video muerto.
        if (Date.now() - inicio >= 4000 && frames >= 30 &&
            smileMax - smileMin < 0.005 && cierreMax - cierreMin < 0.005) {
          return { ok: false, via: 'CONGELADA', smileMin: r4(smileMin), smileMax: r4(smileMax), frames };
        }
      }
    } catch (e) { /* seguir intentando hasta el timeout */ }
    // Pausa mínima: deja respirar a la UI pero maximiza los cuadros analizados por
    // segundo (clave para cachar parpadeos de ~150ms).
    await new Promise((r) => setTimeout(r, 10));
  }
  return { ok: false, via: null, smileMin: smileMin === Infinity ? null : r4(smileMin), smileMax: smileMax ? r4(smileMax) : null, frames };
}

// Distancia 1:1 contra las tomas registradas; devuelve la mejor (menor) y el estado.
export function evaluarMatch(liveDescriptor, tomas) {
  if (!liveDescriptor || !Array.isArray(tomas) || tomas.length === 0) {
    return { estado: 'SIN_MATCH', distancia: null };
  }
  let min = Infinity;
  tomas.forEach((t) => {
    if (t && Array.isArray(t.descriptor) && t.descriptor.length) {
      const d = faceapi.euclideanDistance(liveDescriptor, t.descriptor);
      if (d < min) min = d;
    }
  });
  if (min === Infinity) return { estado: 'SIN_MATCH', distancia: null };
  const estado = min <= 0.5 ? 'ALTA' : (min <= 0.6 ? 'DUDOSA' : 'SIN_MATCH');
  return { estado, distancia: Number(min.toFixed(4)) };
}
